import { times, getPath, pick } from "lib";
import { domParser as defaultDomParser } from "lib/defaultDomParser";
import { getCSSComputedBoundingClientRect } from "lib/htmlElements/dimensions";
import { getClassDefinitionsFromStyleString } from "lib/parseSvgToElement";
import { isHexString } from "lib/parseColor";
import {
  FILL_VALUE_REGEX,
  SVG_FILL_SELECTOR,
  INHERITED_IMAGE_INSTRUCTION_PROPERTIES
} from "lib/constants";
import { isGradientValue } from "lib/gradients/gradientUtils";
import { createSvgGradientDef } from "lib/gradients/svgGradientUtils";

// Isolates the SVG's internal <style> class names from other SVGs by moving
// children into a shadow root. The move happens in connectedCallback so that
// both insertion paths work: the HTML parser's innerHTML upgrade flow (children
// present when the element is upgraded) and imperative createElement +
// appendChild + insert (children attached after construction). `mode: "open"`
// so same-element descents from outside — e.g. HeavyResizePreview — can still
// reach the serialised SVG.
class SVGContainer extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback() {
    if (this.shadowRoot.childNodes.length) return;
    this.shadowRoot.append(...this.childNodes);
  }
}

customElements.define("svg-container", SVGContainer);

export const getSelectedImageInstructionBorder = (svg, domId) => {
  const selected = svg.querySelector("#" + domId);
  if (!selected) return svg;

  const clipPathAttr = selected.getAttribute("clip-path");
  const refMatch = clipPathAttr && clipPathAttr.match(/#(.+)\)/);
  if (!refMatch) return svg;
  const pathIdRef = refMatch[1];

  // Prefix match: ImageInstructionContent mutates the clipPath element's id
  // to `${origId}${element.uniqueId}` to scope it across sibling vectors,
  // but leaves the `clip-path="url(#origId)"` reference on the consuming
  // <g> untouched. An exact `#${origId}` lookup no longer resolves.
  const clipPath = svg.querySelector(`[id^="${pathIdRef}"]`);
  if (!clipPath) return svg;

  svg.querySelectorAll("g").forEach(gNode => {
    if (!gNode.querySelectorAll(`#${domId}`).length) {
      gNode.parentNode.removeChild(gNode);
    }
  });

  if (!svg.contains(clipPath)) return svg;

  [...clipPath.children].forEach(clipPathChild => {
    clipPathChild.setAttribute("stroke", "black");
    clipPathChild.setAttribute("stroke-width", 1);
    clipPathChild.setAttribute("fill", "transparent");
    clipPathChild.setAttribute("vector-effect", "non-scaling-stroke");
    clipPath.parentNode.append(clipPathChild);

    const whiteBorderChild = clipPathChild.cloneNode(true);
    whiteBorderChild.setAttribute("stroke", "white");
    whiteBorderChild.setAttribute("stroke-dasharray", "2.5,2.5");
    clipPath.parentNode.append(whiteBorderChild);
  });

  clipPath.parentNode.removeChild(clipPath);

  return svg;
};

export const generateSvgDomObject = ({
  fillColors,
  width,
  height,
  srcWidth,
  srcHeight,
  scale,
  resizableX,
  resizableY,
  svgDomElement,
  elementId
}) => {
  svgDomElement.setAttribute("id", `${elementId}-svg`);

  // Compute the final viewBox so gradients use userSpaceOnUse with the correct coordinate space.
  // For stretchable SVGs, resizeAndReposition sets viewBox to (width/scale x height/scale).
  // For repeatable SVGs, applyRepeatablePattern sets viewBox to (width/scale x height/scale).
  const stretchable = isStretchableSvg(svgDomElement);
  const repeatable = isRepeatableSvg(svgDomElement);
  const isResizableVector = [resizableX, resizableY].some(x => !!x);
  const unifiedViewBox =
    repeatable && isResizableVector
      ? { width: width / scale, height: height / scale }
      : stretchable && isResizableVector
      ? { width: width / scale, height: height / scale }
      : stretchable
      ? parseViewBox(svgDomElement)
      : null;

  const elements = svgDomElement.querySelectorAll("[id^='change']");
  if (elements.length) {
    // when there is already change ids just use these instead
    applyColors({
      svg: svgDomElement,
      colors: fillColors,
      elementId,
      unifiedViewBox
    });
  } else {
    // set up fill section color ids for this element
    setFillColorIds({ svg: svgDomElement });
    applyColorsForUploaded({
      svg: svgDomElement,
      colors: fillColors,
      elementId,
      unifiedViewBox
    });
  }

  const resizeWidth = isResizableVector ? width : srcWidth * scale;
  const resizeHeight = isResizableVector ? height : srcHeight * scale;

  resizeAndReposition({
    svg: svgDomElement,
    width: resizeWidth,
    height: resizeHeight,
    srcWidth,
    srcHeight,
    scale,
    resizableX,
    resizableY,
    elementId
  });

  applyRepeatablePattern({ svg: svgDomElement, width, height, scale });

  setShapeRendering({ svg: svgDomElement, stretchable });

  const svgStyleElement = svgDomElement.querySelector("style");

  if (svgStyleElement) {
    const svgWrapper = document.createElement("svg-container");

    svgWrapper.appendChild(svgDomElement);

    return svgWrapper;
  }

  return svgDomElement;
};

const setFillColorIds = ({ svg }) => {
  const svgStyleElement = svg.querySelector("style");

  // generalized index to avoid overlapping fill color ids
  let currentIdIndex = 1;

  if (svgStyleElement) {
    const styleString = svgStyleElement.textContent;

    const classDefinitions = getClassDefinitionsFromStyleString(styleString);

    if (Object.keys(classDefinitions).length) {
      Object.keys(classDefinitions).forEach(className => {
        const fillSections = svg.querySelectorAll(`*[class="${className}"]`);
        fillSections.forEach(fillSection => {
          fillSection.setAttribute("id", `change_${currentIdIndex}`);
          currentIdIndex++;
        });
      });
    }
  }

  let fillSections = svg.querySelectorAll(SVG_FILL_SELECTOR);
  if (!fillSections || fillSections.length === 0) {
    return;
  }
  fillSections = Array.from(fillSections).filter(fillSection => {
    const styleAttribute = fillSection.getAttribute("style");
    const styleFillMatch = styleAttribute
      ? styleAttribute.match(FILL_VALUE_REGEX)
      : [];
    const styleFillValue =
      styleFillMatch && styleFillMatch.length > 0 ? styleFillMatch[1] : "";
    const fillAttributeValue = fillSection.getAttribute("fill");
    // we prefer the fill attribute but will use the style value if it is not present
    const fillValue = fillAttributeValue || styleFillValue;
    return isHexString(fillValue);
  });
  fillSections.forEach(fillSection => {
    fillSection.setAttribute("id", `change_${currentIdIndex}`);
    currentIdIndex++;
  });
};

const setShapeRendering = ({ svg, stretchable }) => {
  if (stretchable || !svg.getAttribute("shape-rendering")) {
    svg.setAttribute("shape-rendering", "auto");
  }
};

export const isStretchableSvg = svg => {
  return svg.querySelectorAll("#T, #TR, #L, #C, #R").length > 0;
};

export const isRepeatableSvg = svg => {
  return !!svg.querySelector("#PATTERN");
};

export const parseViewBox = svg => {
  const viewBox = svg.getAttribute("viewBox");
  if (!viewBox) return null;
  const parts = viewBox.split(/\s+/);
  if (parts.length < 4) return null;
  return { width: parseFloat(parts[2]), height: parseFloat(parts[3]) };
};

export const getBaseDomId = domId => {
  // Match "change_N" as the base, stripping any trailing "_N_" suffixes
  const match = domId.match(/^(change_\d+?)(?:_\d+_)*$/);
  if (match && match[1] !== domId) return match[1];
  return domId;
};

const ensureDefsSectionExists = svg => {
  let defs = svg.querySelector("defs");
  if (!defs) {
    defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    svg.insertBefore(defs, svg.firstChild);
  }
  return defs;
};

const applyGradientFill = ({
  svg,
  domId,
  color,
  elementId,
  selector,
  viewBox,
  sharedGradientId
}) => {
  const gradientId = sharedGradientId || `gradient-${elementId}-${domId}`;
  const defs = ensureDefsSectionExists(svg);

  // Only create the gradient def if it doesn't already exist
  if (!defs.querySelector(`#${gradientId}`)) {
    const defString = createSvgGradientDef(color, gradientId, viewBox);
    const tempSvg = document.createElementNS(
      "http://www.w3.org/2000/svg",
      "svg"
    );
    tempSvg.innerHTML = defString;
    const gradientEl = tempSvg.firstElementChild;
    if (gradientEl) {
      defs.appendChild(gradientEl);
    }
  }

  // Apply the gradient fill to the target elements
  const domElements = svg.querySelectorAll(selector);
  if (domElements) {
    domElements.forEach(domElement => {
      domElement.style.fill = `url(#${gradientId})`;
    });
  }
};

const applyColors = ({
  svg,
  colors = [],
  elementId = "",
  unifiedViewBox = null
}) => {
  colors.forEach(({ domId, color }) => {
    if (isGradientValue(color)) {
      const baseDomId = unifiedViewBox ? getBaseDomId(domId) : domId;
      applyGradientFill({
        svg,
        domId,
        color,
        elementId,
        selector: `#${domId} *`,
        viewBox: unifiedViewBox,
        sharedGradientId: unifiedViewBox
          ? `gradient-${elementId}-${baseDomId}`
          : undefined
      });
    } else {
      const domElements = svg.querySelectorAll(`#${domId} *`);
      if (!domElements) return;
      domElements.forEach(domElement => (domElement.style.fill = color));
    }
  });
};

const applyColorsForUploaded = ({
  svg,
  colors = [],
  elementId = "",
  unifiedViewBox = null
}) => {
  colors.forEach(({ domId, color }) => {
    if (isGradientValue(color)) {
      const baseDomId = unifiedViewBox ? getBaseDomId(domId) : domId;
      applyGradientFill({
        svg,
        domId,
        color,
        elementId,
        selector: `#${domId}`,
        viewBox: unifiedViewBox,
        sharedGradientId: unifiedViewBox
          ? `gradient-${elementId}-${baseDomId}`
          : undefined
      });
    } else {
      const domElement = svg.querySelector(`#${domId}`);
      if (!domElement) return;
      domElement.style.fill = color;
    }
  });
};

const applyRepeatablePattern = ({
  svg,
  width,
  height,
  scale,
  domParser = defaultDomParser
}) => {
  const pattern = svg.querySelector("#PATTERN");
  if (!pattern) return svg;

  // Step #1 make pattern id uniq to avoid conflicts with other SVGs
  const salt = Math.random()
    .toString()
    .substring(3);

  pattern.setAttribute("id", pattern.getAttribute("id") + salt);

  // Step #2 rename pattern tag
  // <svg><pattern id="pat-1">XYZ</pattern></svg>
  // TO
  // <svg><g id="pat-1">XYZ</g></svg>

  const patternId = pattern.getAttribute("id");
  const patternWidth = parseFloat(pattern.getAttribute("width"));
  const patternHeight = parseFloat(pattern.getAttribute("height"));

  const gElSrc = pattern.outerHTML
    .replace(/^<pattern/, "<g")
    .replace(/<\/pattern>$/, "</g>");

  const g = domParser
    .parseFromString(gElSrc, "image/svg+xml")
    .querySelector("g");

  pattern.replaceWith(g);

  // Step #3
  // create <use /> element
  const use = domParser
    .parseFromString("<use/>", "image/svg+xml")
    .querySelector("use");

  use.setAttribute("xlink:href", `#${patternId}`);
  use.setAttribute("height", patternHeight);
  use.setAttribute("width", patternWidth);

  const repeatEl = svg.querySelector("#REPEAT_X");

  repeatEl.setAttribute("transform", "translate(0, 0)");

  const elementsCount = Math.ceil(width / scale / patternWidth);

  // Check if any gradient fills are present in the pattern content
  const defs = svg.querySelector("defs");
  const gradients = defs
    ? Array.from(defs.querySelectorAll("linearGradient, radialGradient"))
    : [];

  if (gradients.length > 0) {
    // When gradients use userSpaceOnUse, <use> elements create separate coordinate
    // spaces per tile, causing each tile to show the same gradient portion.
    // Instead, deep-clone the content and create per-tile gradient definitions
    // with gradientTransform to offset coordinates for each tile's position.
    times(elementsCount, index => {
      const tileOffset = patternWidth * index;
      const tileGroup = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "g"
      );
      tileGroup.setAttribute("transform", `translate(${tileOffset},0)`);

      // Deep clone the pattern content into this tile
      Array.from(g.children).forEach(child => {
        tileGroup.appendChild(child.cloneNode(true));
      });

      // Create per-tile gradient definitions with offset transforms
      gradients.forEach(gradientEl => {
        const tileGradientId = `${gradientEl.id}-tile-${index}`;
        const tileGradient = gradientEl.cloneNode(true);
        tileGradient.setAttribute("id", tileGradientId);
        tileGradient.setAttribute(
          "gradientTransform",
          `translate(${-tileOffset}, 0)`
        );
        defs.appendChild(tileGradient);

        // Update fill references in this tile to use the tile-specific gradient
        const originalId = gradientEl.id;
        tileGroup.querySelectorAll("*").forEach(el => {
          if (el.style.fill && el.style.fill.includes(originalId)) {
            el.style.fill = `url(#${tileGradientId})`;
          }
        });
      });

      repeatEl.append(tileGroup);
    });

    // Remove the original pattern group since we're using inline clones
    g.remove();
  } else {
    // No gradients: use <use> elements for efficiency (original behavior)
    times(elementsCount, index => {
      const tempUseEl = use.cloneNode();
      tempUseEl.setAttribute(
        "transform",
        `translate(${patternWidth * index},0)`
      );
      tempUseEl.setAttribute("x", 0);
      tempUseEl.setAttribute("y", 0);
      repeatEl.append(tempUseEl);
    });
  }

  // Step #5
  // adjust viewBoxWidth
  const viewBoxWidth = width / scale;
  const viewBoxHeight = height / scale;

  // some svg has viewbox instead of viewBox, so here is the clear to keep only one
  svg.removeAttribute("viewbox");
  svg.setAttribute("viewBox", `0 0 ${viewBoxWidth} ${viewBoxHeight}`);

  // remove unnecessary elements (fix for Safari)
  svg.querySelector("#REPEAT_X rect").remove();

  return svg;
};

const stretchSvg = ({
  svg,
  width,
  height,
  srcWidth,
  scale,
  srcHeight,
  resizableX,
  resizableY,
  elementId
}) => {
  /* Some Vectors have been created using the structure below,
   * the idea behind it is to be able to skew the vector keeping its
   * corners unchanged(only central elements are stretched), in summary
   * it makes frames scales without distorting its borders */

  /**********
   * TL T TR *
   * L  C  R *
   * BL B BR *
   ***********/

  // Detect gradient fills early — for non-rectangular gradient sections,
  // overlap=0 produces fainter anti-aliasing seams than overlap=1's
  // doubled-opacity lines. For rectangular gradient sections (background
  // rect path), overlap is irrelevant since section fills are cleared.
  const defs = svg.querySelector("defs");
  const gradients = defs
    ? Array.from(defs.querySelectorAll("linearGradient, radialGradient"))
    : [];
  const hasGradients = gradients.length > 0;
  const overlap = hasGradients ? 0 : 1;

  // Horizontal Transform variables
  const viewBoxWidth = width / scale; // apply scale factor to the width
  const originOffsetX = srcWidth / 3; // svg should be split into 3 columns
  const originMiddleColumnWidth = srcWidth - originOffsetX * 2; // width for middle column in the original svg scale
  const expectedMiddleColumnWidth =
    viewBoxWidth - originOffsetX * 2 + overlap * 2; // width for the middle column in the new svg
  const resizeScaleX = resizableX
    ? expectedMiddleColumnWidth / originMiddleColumnWidth
    : 1; // difference in width from original to new
  const offsetX = originOffsetX * (resizeScaleX - 1); // the original offset multiplied by scale factor to find new svg offsetX

  // Vertical Transform variables
  const viewBoxHeight = height / scale; // apply scale factor to the height
  const originOffsetY = srcHeight / 3; // svg should be split into 3 rows
  const originMiddleRowHeight = srcHeight - originOffsetY * 2; // height for middle row in original svg scale
  const expectedMiddleRowHeight =
    viewBoxHeight - originOffsetY * 2 + overlap * 2; // height for the middle row in the new svg
  const resizeScaleY = resizableY
    ? expectedMiddleRowHeight / originMiddleRowHeight
    : 1; // difference in height from original to new
  const offsetY = originOffsetY * (resizeScaleY - 1); // the original offset multiplied by scale factor to find new svg offsetY

  // define if we only need to resize X or Y, otherwise we need to offset both
  const onlyXResize = resizableX && !resizableY;
  const onlyYResize = !resizableX && resizableY;

  // Transform components per section (used for both transform strings and gradient correction)
  const transformComponents = {
    TL: { tx: 0, ty: 0, sx: 1, sy: 1 },
    T: {
      tx: onlyYResize ? 0 : -offsetX - overlap,
      ty: 0,
      sx: resizeScaleX,
      sy: 1
    },
    TR: { tx: offsetX - overlap * 2, ty: 0, sx: 1, sy: 1 },
    L: {
      tx: 0,
      ty: onlyXResize ? 0 : -offsetY - overlap,
      sx: 1,
      sy: resizeScaleY
    },
    C: {
      tx: onlyYResize ? 0 : -offsetX - overlap,
      ty: onlyXResize ? 0 : -offsetY - overlap,
      sx: resizeScaleX,
      sy: resizeScaleY
    },
    R: {
      tx: offsetX - overlap * 2,
      ty: onlyXResize ? 0 : -offsetY - overlap,
      sx: 1,
      sy: resizeScaleY
    },
    BL: { tx: 0, ty: offsetY - overlap * 2, sx: 1, sy: 1 },
    B: {
      tx: onlyYResize ? 0 : -offsetX - overlap,
      ty: offsetY - overlap * 2,
      sx: resizeScaleX,
      sy: 1
    },
    BR: { tx: offsetX - overlap * 2, ty: offsetY - overlap * 2, sx: 1, sy: 1 }
  };

  const transformMap = {
    /* TOP  */ T: `translate(${transformComponents.T.tx} ${transformComponents.T.ty}) scale(${transformComponents.T.sx} ${transformComponents.T.sy})`,
    /* TOP RIGHT  */ TR: `translate(${transformComponents.TR.tx} ${transformComponents.TR.ty})`,
    /* LEFT */ L: `translate(${transformComponents.L.tx} ${transformComponents.L.ty}) scale(${transformComponents.L.sx} ${transformComponents.L.sy})`,
    /* CENTER  */ C: `translate(${transformComponents.C.tx} ${transformComponents.C.ty}) scale(${transformComponents.C.sx} ${transformComponents.C.sy})`,
    /* RIGHT  */ R: `translate(${transformComponents.R.tx} ${transformComponents.R.ty}) scale(${transformComponents.R.sx} ${transformComponents.R.sy})`,
    /* BOTTOM LEFT  */ BL: `translate(${transformComponents.BL.tx} ${transformComponents.BL.ty})`,
    /* BOTTOM  */ B: `translate(${transformComponents.B.tx} ${transformComponents.B.ty})  scale(${transformComponents.B.sx} ${transformComponents.B.sy})`,
    /* BOTTOM RIGHT  */ BR: `translate(${transformComponents.BR.tx} ${transformComponents.BR.ty})`,
    /* TOP LEFT */ TL: ""
  };

  const overlayOffsetX = onlyXResize ? 0 : 3;
  const overlayOffsetY = onlyYResize ? 0 : 3;

  const overlapMap = {
    TL: { x: `${overlayOffsetX}`, y: `${overlayOffsetY}` },
    T: { x: "0", y: `${overlayOffsetY}` },
    TR: { x: `-${overlayOffsetX}`, y: `${overlayOffsetY}` },
    L: { x: `${overlayOffsetX}`, y: "0" },
    C: { x: "0", y: "0" },
    R: { x: `-${overlayOffsetX}`, y: "0" },
    BL: { x: `${overlayOffsetX}`, y: `-${overlayOffsetY}` },
    B: { x: "0", y: `-${overlayOffsetY}` },
    BR: { x: `-${overlayOffsetX}`, y: `-${overlayOffsetY}` }
  };

  // need to check if the center section has no fill value
  const centerSection = svg.querySelector(`#C`);
  const centerChild = getPath(centerSection, "firstChild");
  const centerChildT2 = getPath(centerChild, "firstChild");

  const isCenterEmpty =
    !centerSection ||
    (centerChildT2 &&
      ["g", "rect"].includes(centerChildT2.type) &&
      centerChildT2.getAttribute("fill") === "none") ||
    (centerChild &&
      ["g", "rect"].includes(centerChild.type) &&
      centerChild.getAttribute("fill") === "none");

  // Check for gradient fills (overlap offsets shift gradients, causing visible seams)
  // hasGradients, defs, and gradients are computed at the top of stretchSvg

  // apply the transforms for the stretching
  Object.keys(transformMap).forEach(transformKey => {
    const currentSection = svg.querySelector(`#${transformKey}`);

    // make sure the section exists before trying to modify it
    if (currentSection) {
      const newSectionId = `${transformKey}-${elementId}`;

      // apply a new unique id to this section
      currentSection.setAttribute("id", newSectionId);

      // apply transform to section
      currentSection.setAttribute("transform", transformMap[transformKey]);

      // clone the section with use tags
      const useElem = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "use"
      );
      useElem.setAttributeNS(
        "http://www.w3.org/1999/xlink",
        "xlink:href",
        `#${newSectionId}`
      );

      const isCurrentSectionEmpty =
        getPath(currentSection, "firstChild.fill", "") !== "none" &&
        getPath(currentSection, "firstChild.firstChild.fill", "") !== "none";

      // Skip overlap for gradient-filled sections: the offset shifts the
      // userSpaceOnUse gradient coordinates in the backing <use> element,
      // creating a visible colour seam at section boundaries.
      const sectionHasGradientFill =
        hasGradients &&
        Array.from(currentSection.querySelectorAll("*")).some(
          el => el.style && el.style.fill && el.style.fill.includes("url(")
        );

      const isOffsetRequired =
        !isCenterEmpty && !isCurrentSectionEmpty && !sectionHasGradientFill;

      // do not apply offset to backing if the element has no fill (transparent inner or background)
      if (isOffsetRequired) {
        useElem.setAttribute("x", overlapMap[transformKey].x);
        useElem.setAttribute("y", overlapMap[transformKey].y);
      }

      if (currentSection.parentNode === svg) {
        svg.insertBefore(useElem, currentSection);
      }
    }
  });

  // For gradient fills on stretchable SVGs, we need to handle two cases:
  //
  // 1. Rectangular sections (all rects): render gradient as a single background
  //    rect covering the viewBox. This eliminates seam lines completely since
  //    there's only one shape — no section boundaries to create artifacts.
  //
  // 2. Non-rectangular sections (polygons/paths): fall back to per-section
  //    gradient copies with gradientTransform correction. This may show faint
  //    seams with semi-transparent gradients, but preserves correct shapes.
  if (hasGradients) {
    gradients.forEach(gradientEl => {
      // Collect all gradient-filled elements across sections
      const allGradientEls = [];
      Object.keys(transformComponents).forEach(sectionKey => {
        const sectionId = `${sectionKey}-${elementId}`;
        const section = svg.querySelector(`#${sectionId}`);
        if (!section) return;
        section.querySelectorAll("*").forEach(el => {
          if (
            el.style &&
            el.style.fill &&
            el.style.fill.includes(gradientEl.id)
          ) {
            allGradientEls.push(el);
          }
        });
      });

      // Check if all gradient-filled shapes are simple rects
      const allRects =
        allGradientEls.length > 0 &&
        allGradientEls.every(el => el.tagName.toLowerCase() === "rect");

      if (allRects) {
        // CASE 1: All rects — use single background rect for seamless gradient
        const bgRect = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "rect"
        );
        bgRect.setAttribute("x", "0");
        bgRect.setAttribute("y", "0");
        bgRect.setAttribute("width", String(viewBoxWidth));
        bgRect.setAttribute("height", String(viewBoxHeight));
        bgRect.setAttribute("fill", `url(#${gradientEl.id})`);

        // Insert right after <defs> so it renders behind all sections
        svg.insertBefore(bgRect, defs.nextSibling);

        // Clear gradient fills from section shapes
        allGradientEls.forEach(el => {
          el.style.fill = "none";
        });
        // Keep original gradient — it's used by the background rect
      } else {
        // CASE 2: Non-rect shapes — per-section gradient with gradientTransform
        Object.keys(transformComponents).forEach(sectionKey => {
          const { tx, ty, sx, sy } = transformComponents[sectionKey];
          const sectionId = `${sectionKey}-${elementId}`;
          const section = svg.querySelector(`#${sectionId}`);
          if (!section) return;

          const usesGradient = Array.from(section.querySelectorAll("*")).some(
            el =>
              el.style && el.style.fill && el.style.fill.includes(gradientEl.id)
          );
          if (!usesGradient) return;

          const sectionGradientId = `${gradientEl.id}-${sectionKey}`;
          const sectionGradient = gradientEl.cloneNode(true);
          sectionGradient.setAttribute("id", sectionGradientId);

          const needsTransform = tx !== 0 || ty !== 0 || sx !== 1 || sy !== 1;
          if (needsTransform) {
            sectionGradient.setAttribute(
              "gradientTransform",
              `scale(${1 / sx} ${1 / sy}) translate(${-tx} ${-ty})`
            );
          }

          defs.appendChild(sectionGradient);

          section.querySelectorAll("*").forEach(el => {
            if (
              el.style &&
              el.style.fill &&
              el.style.fill.includes(gradientEl.id)
            ) {
              el.style.fill = `url(#${sectionGradientId})`;
            }
          });
        });

        // Remove the original shared gradient (replaced by per-section copies)
        gradientEl.remove();
      }
    });
  }

  // set the viewbox for the new transformed svg
  svg.setAttribute("viewBox", `0 0 ${viewBoxWidth} ${viewBoxHeight}`);
};

const resizeAndReposition = ({
  svg,
  width,
  height,
  srcWidth,
  srcHeight,
  scale,
  resizableX,
  resizableY,
  elementId
}) => {
  svg.setAttribute("width", width);
  svg.setAttribute("height", height);

  if (svg.querySelectorAll("#T, #TR, #L, #C, #R").length) {
    svg.setAttribute("viewBox", `0 0  ${width / scale} ${height / scale}`);

    return stretchSvg({
      svg,
      width,
      height,
      srcWidth,
      scale,
      srcHeight,
      resizableX,
      resizableY,
      elementId
    });
  }
};

export const getSize = el => {
  if (!el) return { top: 0, left: 0, width: 0, height: 0 };

  const left = parseFloat(el.getAttribute("x"));
  const top = parseFloat(el.getAttribute("y"));
  const width = parseFloat(el.getAttribute("width"));
  const height = parseFloat(el.getAttribute("height"));

  return { top, left, width, height };
};

export const getFrameDimensions = ({
  vectorId,
  frameId,
  vectorElement,
  zoom
}) => {
  // ratio is scale factor from original size to current size
  const vectorRatio = vectorElement.width / vectorElement.srcWidth; //<design space>
  const vectorRatioZoomed = vectorRatio * zoom; //<window space>

  // get the DOM node for the element
  const DOMElement = document.querySelector(`[id="${vectorId}"]`);
  if (!DOMElement)
    return {
      width: 0,
      height: 0
    };

  const clipPathGTag = DOMElement.querySelector(`[id="${frameId}"]`);

  if (!clipPathGTag) {
    return;
  }

  // back track to closest clip-path tag
  const clipPath = clipPathGTag.parentElement.querySelector("clipPath");

  const getDimensionSource = () => {
    const rect = clipPath.querySelector("rect");
    const circle = clipPath.querySelector("circle");
    const path = clipPath.querySelector("path");
    const ellipse = clipPath.querySelector("ellipse");
    const polygon = clipPath.querySelector("polygon");
    return rect || circle || path || ellipse || polygon;
  };

  // define the object to get dimensions for dropzone from
  const dimensionSource = getDimensionSource();

  return getCSSComputedBoundingClientRect({
    element: dimensionSource,
    cssValueMultiplier: vectorRatioZoomed
  });
};

export const calculateUpdatedVectorImageInstructions = async ({
  imageInstruction,
  originalImageInstruction,
  maskSize
}) => {
  await imageInstruction.imageElementBuilderPromise;

  const scaleToFit = Math.max(
    maskSize.width / imageInstruction.width,
    maskSize.height / imageInstruction.height
  );

  const imageScaled = {
    height: imageInstruction.height * scaleToFit,
    width: imageInstruction.width * scaleToFit
  };

  // When replacing an image instruction, ensure originalSrc is updated to the new source
  const updatedSrc = imageInstruction.originalSrc || imageInstruction.src;

  return {
    ...originalImageInstruction,
    ...imageInstruction,
    ...imageScaled,
    top: (maskSize.height - imageScaled.height) / 2,
    left: (maskSize.width - imageScaled.width) / 2,
    ...pick(originalImageInstruction, INHERITED_IMAGE_INSTRUCTION_PROPERTIES),
    originalSrc: updatedSrc
  };
};

export const getMaskSizeForVector = (elementData, domId) => {
  const svgDOMElement = defaultDomParser
    .parseFromString(window.easil.svgs[elementData.src].data, "image/svg+xml")
    .querySelector("svg");

  if (!svgDOMElement) {
    return;
  }

  const svgImageTag = svgDOMElement.querySelector(`#${domId} > image`);

  if (!svgImageTag) {
    return;
  }

  return {
    width: svgImageTag.getAttribute("width"),
    height: svgImageTag.getAttribute("height")
  };
};

export const validateImageInstructions = imageInstruction => {
  const mandatoryFields = ["srcHeight", "srcWidth", "media", "scale"];

  return mandatoryFields.every(field => imageInstruction.hasOwnProperty(field));
};

export const applyColorsExported = applyColors;
export const applyColorsForUploadedExported = applyColorsForUploaded;
